import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { ResetButtonComponent } from "@components/reset-button/reset-button.component";
import { DeviceType } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep, StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { AutocompleteSearchFilterPipe } from "@odx/angular/components/autocomplete";
import { ContentBoxComponent } from "@odx/angular/components/content-box";
import { FormFieldComponent, FormFieldControlDirective } from "@odx/angular/components/form-field";
import { RadioGroupComponent } from "@odx/angular/components/radio-group";
import { SelectComponent, SelectOptionComponent } from "@odx/angular/components/select";
import { untilDestroyed } from "@odx/angular/utils";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { getMonitoringCategoryDataType } from "@project/monitoring/monitoring-family-wizard.utils";
import { MonitoringOptionCardWithQuantityComponent } from "@project/monitoring/monitoring-option-card-with-quantity/monitoring-option-card-with-quantity.component";
import { MonitoringOptionCardComponent } from "@project/monitoring/monitoring-option-card/monitoring-option-card.component";
import { OptionDisplay, OptionDisplayService } from "@project/monitoring/option-display.service";
import { BehaviorSubject, combineLatest, forkJoin, ReplaySubject } from "rxjs";
import { getConfigurationOptionIdentifier } from "../../monitoring/monitoring-configuration.utils";

export interface OptionDisplayWithQuantity {
  optionDisplay: OptionDisplay;
  quantity: number;
}

@Component({
  standalone: true,
  selector: "app-option-selector",
  imports: [
    ContentBoxComponent,
    FilterProductComponent,
    RadioGroupComponent,
    ReactiveFormsModule,
    MonitoringOptionCardComponent,
    CommonModule,
    MonitoringOptionCardWithQuantityComponent,
    ResetButtonComponent,
    AutocompleteSearchFilterPipe,
    FormFieldComponent,
    SelectComponent,
    FormFieldControlDirective,
    SelectOptionComponent,
    UiKitModule,
    RefreshProductsHintComponent,
  ],
  templateUrl: "./option-selector.component.html",
  styleUrl: "./option-selector.component.scss",
})
export class OptionSelectorComponent implements OnDestroy, OnChanges {
  //Workaround for stepSelectionType pick1toN since the failed validation is not shown correctly for required odx checkboxes otherwise
  @HostBinding("class.validation-invalid")
  get isValidationInvalid(): boolean {
    const form =
      this.step?.stepSelectionType === StepSelectionType.PICK0TON || this.step?.stepSelectionType === StepSelectionType.PICK1TON
        ? this.optionFormMultiple
        : this.optionFormSingle;
    return form.invalid && form.touched;
  }

  private monitoringProductService = inject(MonitoringProductService);
  private optionDisplayService = inject(OptionDisplayService);

  @Input() selectedFamily!: Family;
  @Input({ required: true }) category!: MonitoringCategory;
  @Input() categoryLabel?: string;
  @Input() step?: FamilyStep;
  @Input() precomputedOptionDisplays?: OptionDisplay[];
  @Input() originalOptions?: MonitoringProductItem[];
  @Input() hasQuantities?: boolean = false;
  @Input() deviceTypeFilter?: DeviceType;
  @Input() allowedDeviceIds?: string[];
  @Input() autoSelectSingleOption: boolean = true;
  @Input() filter1Label?: string;
  @Input() filter1InputPlaceholder?: string;
  @Input() filter2Label?: string;
  @Input() filter2InputPlaceholder?: string;

  private _step!: FamilyStep;
  private readonly takeUntilDestroyed = untilDestroyed();

  @Input() title!: string;

  @Output() optionsSelect$ = new EventEmitter<OptionDisplayWithQuantity[]>();

  protected selectedOptions?: OptionDisplay[];

  private formBuilder = inject(FormBuilder);

  protected optionFormSingle = this.formBuilder.group({
    selectedOptionSingle: new FormControl<null | OptionDisplay>(null),
  });

  protected optionFormMultiple = this.formBuilder.group({
    selectedOptionMultiple: new FormControl<null | OptionDisplay[]>(null),
  });

  protected additionalFilterForm = this.formBuilder.group({
    filter1: new FormControl<string[] | null>(null),
    filter2: new FormControl<string[] | null>(null),
  });

  @Output()
  formReady = new EventEmitter<any>();

  protected readonly optionDisplays$ = new ReplaySubject<OptionDisplay[]>(1);
  protected readonly filter1Options$ = new ReplaySubject<string[]>(1);
  protected readonly filter2Options$ = new ReplaySubject<string[]>(1);

  protected optionDisplays: OptionDisplay[] = [];
  protected readonly filteredOptionDisplays$ = new ReplaySubject<OptionDisplay[]>(1);
  protected readonly StepSelectionType = StepSelectionType;

  private readonly searchValue$ = new BehaviorSubject<string | null>(null);
  private readonly filter1Value$ = new BehaviorSubject<string[] | null>(null);
  private readonly filter2Value$ = new BehaviorSubject<string[] | null>(null);

  protected filter1DefaultLabel = $localize`:@@optionConfiguration.filter1.label.default:Filter auswählen`;
  protected filter1DefaultInputPlaceholder = $localize`:@@optionConfiguration.filter1.inputPlaceholder.default:Filter auswählen`;

  protected filter2DefaultLabel = $localize`:@@optionConfiguration.filter2.label.default:Filter auswählen`;
  protected filter2DefaultInputPlaceholder = $localize`:@@optionConfiguration.filter2.inputPlaceholder.default:Filter auswählen`;

  private allOptionDisplays: OptionDisplay[] = [];
  private readonly destroyRef = inject(DestroyRef);

  private readonly selectedQuantities = signal(new Map<string, number>());

  protected get refreshProductsDataType(): string {
    return getMonitoringCategoryDataType(this.category);
  }

  constructor() {
    combineLatest([this.searchValue$, this.filter1Value$, this.filter2Value$, this.optionDisplays$]).subscribe(
      ([searchValue, filter1Value, filter2Value, optionDisplays]) => {
        let filteredOptions = optionDisplays as OptionDisplay[];
        if (filter1Value && filter1Value.length > 0) {
          filteredOptions = filteredOptions.filter((option) => filter1Value.includes(option.option.filter1 ?? ""));
        }
        if (filter2Value && filter2Value.length > 0) {
          filteredOptions = filteredOptions.filter((option) => filter2Value.includes(option.option.filter2 ?? ""));
        }
        if (searchValue) {
          filteredOptions = filteredOptions.filter((option) =>
            this.monitoringProductService.searchFilterOption(option, searchValue),
          );
        }
        this.filteredOptionDisplays$.next(filteredOptions);
      },
    );

    this.optionFormSingle.valueChanges.pipe(this.takeUntilDestroyed()).subscribe(() => {
      const selectedOption = this.optionFormSingle.value.selectedOptionSingle;
      if (!selectedOption) {
        this.selectedOptions = undefined;
        this.optionsSelect$.emit([]);
        return;
      }

      this.selectedOptions = [selectedOption];
      this.optionsSelect$.emit([
        {
          optionDisplay: selectedOption,
          quantity: this.selectedQuantities().get(this.optionKey(selectedOption.option)) as number,
        },
      ]);
    });

    this.optionFormMultiple.valueChanges.pipe(this.takeUntilDestroyed()).subscribe(() => {
      const raw = this.optionFormMultiple.value.selectedOptionMultiple;
      const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];

      this.selectedOptions = selected;
      const selections = selected.map((optionDisplay: OptionDisplay) => {
        const quantity = this.selectedQuantities().get(this.optionKey(optionDisplay.option)) ?? 1;

        return { optionDisplay, quantity };
      });
      this.optionsSelect$.emit(selections);
    });

    this.additionalFilterForm
      .get("filter1")
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.filter1Value$.next(val);
      });

    this.additionalFilterForm
      .get("filter2")
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.filter2Value$.next(val);
      });
  }

  ngOnDestroy() {
    this.filteredOptionDisplays$.complete();
    this.optionDisplays$.complete();
    this.searchValue$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.category) return;

    //if create option displays for each option of the current step
    if (changes["step"] || changes["category"] || changes["selectedFamily"] || changes["precomputedOptionDisplays"]) {
      this.resetSelectionState();
      if (this.step) {
        this.setSingleSelectionFormValidators();
        this.setMultipleSelectionFormValidators();
        this._step = this.step;
        if (
          this.step.stepSelectionType === StepSelectionType.PICK1 ||
          this.step.stepSelectionType === StepSelectionType.PICK0TO1
        ) {
          this.formReady.emit(this.optionFormSingle);
        } else {
          this.formReady.emit(this.optionFormMultiple);
        }
        if (this.step.options.length === 0) {
          this.allOptionDisplays = [];
          this.applyDeviceTypeFilter();
          this.initFilters([]);
          this.tryRestoreOriginalOptions();
        } else if (this.precomputedOptionDisplays) {
          this.allOptionDisplays = this.precomputedOptionDisplays;
          this.applyDeviceTypeFilter();
          this.initFilters(this.step!.options);
          this.tryRestoreOriginalOptions();
        } else {
          const optionDisplaysObservables = this.step.options.map((option) =>
            this.optionDisplayService.createOptionDisplay(option, this.category, this.selectedFamily),
          );

          forkJoin(optionDisplaysObservables).subscribe((results) => {
            this.allOptionDisplays = results;
            this.applyDeviceTypeFilter();
            this.initFilters(this.step!.options);
            this.tryRestoreOriginalOptions();
          });
        }
      }
    }

    if (changes["deviceTypeFilter"] || changes["allowedDeviceIds"] || changes["autoSelectSingleOption"]) {
      this.applyDeviceTypeFilter();
    }

    //creates option display(s) for an original configuration and sets the created display(s) as value for the respective from group depending on step selection type
    if (changes["originalOptions"] || changes["category"]) {
      this.tryRestoreOriginalOptions();
    }
  }

  protected getSelectedQuantity(option: FamilyStepOption): number {
    return this.selectedQuantities().get(this.optionKey(option)) ?? 0;
  }

  protected updateSelectedQuantity(quantity: number, option: OptionDisplay): void {
    if (!this.step) return;
    const key = this.optionKey(option.option);
    const next = new Map(this.selectedQuantities());

    if (
      this.step.stepSelectionType === StepSelectionType.PICK0TON ||
      this.step.stepSelectionType === StepSelectionType.PICK1TON
    ) {
      if (quantity <= 0) next.delete(key);
      else next.set(key, quantity);

      this.selectedQuantities.set(next);
    } else if (this.step.stepSelectionType === StepSelectionType.PICK1) {
      const next = new Map<string, number>();

      if (quantity >= 1) {
        next.set(key, quantity);
      }

      this.selectedQuantities.set(next);
    }
  }

  private applyDeviceTypeFilter() {
    let filtered = this.allOptionDisplays;

    if (this.deviceTypeFilter) {
      const normalizedFilter = this.normalizeDeviceType(this.deviceTypeFilter);
      filtered = filtered.filter((display) => {
        const candidateTypes =
          display.deviceTypes && display.deviceTypes.length > 0
            ? display.deviceTypes
            : display.deviceType
              ? [display.deviceType]
              : [];

        // Fail open: if no type metadata is available for an option, do not hide it.
        if (candidateTypes.length === 0) {
          return true;
        }

        return candidateTypes.some((type) => this.normalizeDeviceType(type) === normalizedFilter);
      });
    }

    if (this.allowedDeviceIds && this.allowedDeviceIds.length > 0) {
      const allowedDeviceIdSet = new Set(this.allowedDeviceIds);
      filtered = filtered.filter((display) => display.option.devices.some((id) => allowedDeviceIdSet.has(id)));
    }

    this.optionDisplays = filtered;
    this.optionDisplays$.next(filtered);
    // Auto-select when exactly one option remains and no original selection exists.
    if (filtered.length === 1 && this.autoSelectSingleOption && (!this.originalOptions || this.originalOptions.length === 0)) {
      const option = filtered[0];
      const qty = Math.max(option.option.quantityMin ?? 1, 1);
      const selectionType = this.step?.stepSelectionType;

      if (selectionType === StepSelectionType.PICK1 || selectionType === StepSelectionType.PICK0TO1) {
        const current = this.optionFormSingle.value.selectedOptionSingle;
        const isAlreadySelected = !!current && this.optionKey(current.option) === this.optionKey(option.option);
        if (!isAlreadySelected) {
          this.select(option, qty);
        }
      }

      if (selectionType === StepSelectionType.PICK1TON) {
        const current: OptionDisplay[] = this.optionFormMultiple.value.selectedOptionMultiple ?? [];
        const isAlreadySelected = current.some((selected) => this.optionKey(selected.option) === this.optionKey(option.option));

        if (!isAlreadySelected) {
          this.updateSelectedQuantity(qty, option);
          this.optionFormMultiple.setValue({ selectedOptionMultiple: [...current, option] });
        }
      }
    }
  }

  private normalizeDeviceType(deviceType?: string): string | undefined {
    if (!deviceType) return undefined;
    return deviceType.toLowerCase().replace(/[-_]/g, "");
  }

  private optionKey(option: FamilyStepOption): string {
    // Keep key stable even if selectedFamily input changes (e.g. mixed-family transmitter/receiver flow).
    return getConfigurationOptionIdentifier(undefined, option);
  }

  private optionsMatchIgnoringFamily(a: FamilyStepOption, b: FamilyStepOption): boolean {
    const groupA = a.groupName?.trim() ?? "";
    const groupB = b.groupName?.trim() ?? "";

    if (groupA || groupB) {
      return groupA === groupB;
    }

    const devicesA = [...(a.devices ?? [])].map(String).sort().join("|");
    const devicesB = [...(b.devices ?? [])].map(String).sort().join("|");
    return devicesA === devicesB;
  }

  protected select(option: OptionDisplay, quantity: number = 1): void {
    if (!this.step) return;

    if (this.step.stepSelectionType === StepSelectionType.PICK1 || this.step.stepSelectionType === StepSelectionType.PICK0TO1) {
      this.updateSelectedQuantity(quantity, option);
      this.optionFormSingle.patchValue({
        selectedOptionSingle: option,
      });
    } else if (
      this.step.stepSelectionType === StepSelectionType.PICK0TON ||
      this.step.stepSelectionType === StepSelectionType.PICK1TON
    ) {
      const current: OptionDisplay[] = this.optionFormMultiple.value.selectedOptionMultiple ?? [];
      let updated: OptionDisplay[];
      if (current.includes(option)) {
        this.updateSelectedQuantity(0, option);
        updated = current.filter((o) => o !== option);
      } else {
        const initialQuantity = Math.max(quantity, option.option.quantityMin ?? 1, 1);
        this.updateSelectedQuantity(initialQuantity, option);
        updated = [...current, option];
      }

      this.optionFormMultiple.setValue({ selectedOptionMultiple: updated });
    }
  }

  protected updateQuantityForOption(option: OptionDisplay, quantity: number): void {
    if (!this.step) return;

    this.updateSelectedQuantity(quantity, option);

    if (
      this.step.stepSelectionType === StepSelectionType.PICK0TON ||
      this.step.stepSelectionType === StepSelectionType.PICK1TON
    ) {
      const current: OptionDisplay[] = this.optionFormMultiple.value.selectedOptionMultiple ?? [];
      const key = this.optionKey(option.option);
      const trackedQuantity = this.selectedQuantities().get(key);

      if (trackedQuantity === undefined) {
        if (current.includes(option)) {
          this.optionFormMultiple.setValue({ selectedOptionMultiple: current.filter((o) => o !== option) });
        }
        return;
      }

      if (!current.includes(option)) {
        this.optionFormMultiple.setValue({ selectedOptionMultiple: [...current, option] });
        return;
      }

      this.emitMultipleSelections();
      return;
    }

    if (this.step.stepSelectionType === StepSelectionType.PICK1 || this.step.stepSelectionType === StepSelectionType.PICK0TO1) {
      this.optionFormSingle.patchValue({ selectedOptionSingle: option });
      return;
    }

    const current: OptionDisplay[] = this.optionFormMultiple.value.selectedOptionMultiple ?? [];
    this.optionFormMultiple.setValue({ selectedOptionMultiple: [...current] });
  }

  protected onQuantityChanged(option: OptionDisplay, quantity: number): void {
    this.updateQuantityForOption(option, quantity);
  }

  private emitMultipleSelections(): void {
    const current: OptionDisplay[] = this.optionFormMultiple.value.selectedOptionMultiple ?? [];
    const selections = current.map((optionDisplay: OptionDisplay) => ({
      optionDisplay,
      quantity: this.selectedQuantities().get(this.optionKey(optionDisplay.option)) ?? 1,
    }));
    this.optionsSelect$.emit(selections);
  }
  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected isSelected(option: OptionDisplay) {
    if (!this.selectedOptions) return false;

    return this.selectedOptions.some(
      (s: OptionDisplay) => JSON.stringify(s.option.devices) === JSON.stringify(option.option.devices),
    );
  }

  protected trackOptionDisplay(option: OptionDisplay): string {
    const familyId = option.family?.familyNumber ?? this.selectedFamily?.familyNumber ?? "";
    const deviceIds = option.option.devices.join("|");
    const groupName = option.option.groupName ?? "";
    return `${familyId}::${deviceIds}::${groupName}`;
  }

  protected trackFilterOption(value: unknown, index: number): string {
    if (value === null || value === undefined) {
      return `idx:${index}`;
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    if (typeof value === "object") {
      const entry = value as { id?: unknown; value?: unknown; label?: unknown };
      if (entry.id !== undefined) return String(entry.id);
      if (entry.value !== undefined) return String(entry.value);
      if (entry.label !== undefined) return String(entry.label);
      return JSON.stringify(entry);
    }

    return `idx:${index}`;
  }

  private resetSelectionState() {
    this.selectedOptions = undefined;
    this.selectedQuantities.set(new Map());
    this.optionDisplays = [];
    this.optionDisplays$.next([]);
    this.additionalFilterForm.patchValue({ filter1: null, filter2: null }, { emitEvent: false });
    this.searchValue$.next(null);
    this.filter1Value$.next(null);
    this.filter2Value$.next(null);
    this.optionFormSingle.patchValue({ selectedOptionSingle: null }, { emitEvent: false });
    this.optionFormMultiple.patchValue({ selectedOptionMultiple: null }, { emitEvent: false });
  }

  private tryRestoreOriginalOptions() {
    if (!this.originalOptions || !this.originalOptions.length) {
      // An explicitly empty array means the original configuration was removed – clear the selection.
      // undefined/null means no original configuration was provided – keep current state (e.g. auto-selection).
      if (Array.isArray(this.originalOptions) && this.originalOptions.length === 0) {
        this.selectedOptions = undefined;
        this.selectedQuantities.set(new Map());
        this.optionFormSingle.patchValue({ selectedOptionSingle: null }, { emitEvent: false });
        this.optionFormMultiple.patchValue({ selectedOptionMultiple: null }, { emitEvent: false });
      }
      return;
    }
    if (!this.step) return;
    if (!this.optionDisplays.length) return;

    if (this.step.stepSelectionType === StepSelectionType.PICK1 || this.step.stepSelectionType === StepSelectionType.PICK0TO1) {
      const firstOriginalOption = this.originalOptions[0];
      if (!firstOriginalOption) return;

      const display =
        this.optionDisplays.find(
          (optionDisplay) => this.optionKey(optionDisplay.option) === this.optionKey(firstOriginalOption.option),
        ) ??
        this.optionDisplays.find((optionDisplay) =>
          this.optionsMatchIgnoringFamily(optionDisplay.option, firstOriginalOption.option),
        );
      if (!display) return;

      const next = new Map<string, number>();
      next.set(this.optionKey(display.option), firstOriginalOption.quantity);

      this.selectedQuantities.set(next);
      this.optionFormSingle.patchValue({ selectedOptionSingle: display }, { emitEvent: false });
      this.selectedOptions = [display];
      this.optionsSelect$.emit([
        {
          optionDisplay: display,
          quantity: firstOriginalOption.quantity,
        },
      ]);
      return;
    }

    if (
      this.step.stepSelectionType === StepSelectionType.PICK0TON ||
      this.step.stepSelectionType === StepSelectionType.PICK1TON
    ) {
      const next = new Map<string, number>();
      const displays = this.originalOptions
        .map((originalOption) => {
          const matchedDisplay =
            this.optionDisplays.find(
              (optionDisplay) => this.optionKey(optionDisplay.option) === this.optionKey(originalOption.option),
            ) ??
            this.optionDisplays.find((optionDisplay) =>
              this.optionsMatchIgnoringFamily(optionDisplay.option, originalOption.option),
            );

          if (matchedDisplay) {
            next.set(this.optionKey(matchedDisplay.option), originalOption.quantity);
          }

          return matchedDisplay;
        })
        .filter((display): display is OptionDisplay => !!display);

      this.selectedQuantities.set(next);
      this.optionFormMultiple.patchValue({ selectedOptionMultiple: displays }, { emitEvent: false });
      this.selectedOptions = displays;
      this.optionsSelect$.emit(
        displays.map((optionDisplay) => ({
          optionDisplay,
          quantity: next.get(this.optionKey(optionDisplay.option)) ?? 1,
        })),
      );
    }
  }

  protected resetSingleSelection() {
    this.optionFormSingle.patchValue({ selectedOptionSingle: null }, { emitEvent: false });
    this.optionFormSingle.markAsPristine();
    this.optionFormSingle.markAsUntouched();
    this.selectedOptions = undefined;
    this.selectedQuantities.set(new Map());
    this.optionsSelect$.emit([]);
  }

  private setSingleSelectionFormValidators() {
    if (this.step?.stepSelectionType === StepSelectionType.PICK0TO1) {
      this.optionFormSingle.controls.selectedOptionSingle.clearValidators();
    } else if (this.step?.stepSelectionType === StepSelectionType.PICK1) {
      this.optionFormSingle.controls.selectedOptionSingle.setValidators(Validators.required);
    }
    this.optionFormSingle.controls.selectedOptionSingle.updateValueAndValidity({ emitEvent: false });
  }

  private setMultipleSelectionFormValidators() {
    if (this.step?.stepSelectionType === StepSelectionType.PICK0TON) {
      this.optionFormMultiple.controls.selectedOptionMultiple.clearValidators();
    } else if (this.step?.stepSelectionType === StepSelectionType.PICK1TON) {
      //this.optionFormMultiple.controls.selectedOptionMultiple.setValidators(Validators.required);
      this.optionFormMultiple.controls.selectedOptionMultiple.setValidators([
        Validators.required,
        (control) => {
          const val = control.value;
          return Array.isArray(val) && val.length > 0 ? null : { required: true };
        },
      ]);
    }
    this.optionFormMultiple.controls.selectedOptionMultiple.updateValueAndValidity({ emitEvent: false });
  }

  private initFilters(options: FamilyStepOption[]) {
    const filter1 = this.uniqueSortedValues(options.map((option) => option.filter1));
    const filter2 = this.uniqueSortedValues(options.map((option) => option.filter2));
    this.filter1Options$.next(filter1);
    this.filter2Options$.next(filter2);
  }

  private uniqueSortedValues(values: Array<string | null | undefined>) {
    const filtered = (values ?? []).filter((value): value is string => value !== undefined && value !== null && value !== "");
    const unique = Array.from(new Set(filtered));
    return unique.sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return numA - numB;
      }
      return a.localeCompare(b);
    });
  }
}
